// frontend/src/pages/LoanApplication.js

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  CheckCircle2,
  ClipboardList,
  FileText,
  Phone,
  ShieldCheck,
  Timer,
} from "lucide-react";
import { toast } from "sonner";

import SEO from "../components/SEO";
import api from "../api/axios";

const LoanApplication = () => {
  const waLink = useMemo(() => {
    const text =
      "Hi OJOVEST - I want to apply for a loan. Please guide me on documents and best rate options.";
    return `https://wa.me/918651035362?text=${encodeURIComponent(text)}`;
  }, []);

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "loan",
    source: "loan_application_page",
    message: "",
    loanAmount: "100000", // ₹1 lakh
    loanType: "personal_loan",
    city: "",
  });

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneClean = (form.phone || "").replace(/\s|-/g, "");
    const phoneOk =
      phoneClean.startsWith("+91")
        ? /^\+91\d{10}$/.test(phoneClean)
        : /^\d{10}$/.test(phoneClean);

    if (!phoneOk) {
      toast.error("Please enter a valid Indian phone number (10 digits / +91).");
      return;
    }

    const amountNum = Number(form.loanAmount || 0);
    if (!amountNum || amountNum < 100000) {
      toast.error("Loan amount should be minimum ₹1,00,000 (1 lakh).");
      return;
    }

    try {
      setLoading(true);

      // Send to your existing lead API
      await api.post("/leads/create", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        interest: "loan",
        source: "loan_application_page",
        message: `Loan Type: ${form.loanType}
Loan Amount: ₹${amountNum.toLocaleString("en-IN")}
City: ${form.city || "-"}
Notes: ${form.message || "-"}`,
      });

      toast.success("Submitted! Our team will contact you within 24 hours.");
      setForm((p) => ({
        ...p,
        name: "",
        phone: "",
        email: "",
        message: "",
        loanAmount: "100000",
        loanType: "personal_loan",
        city: "",
      }));
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* ✅ SEO */}
      <SEO
        title="Apply for Loan | OJOVEST"
        description="Apply for premium loans with OJOVEST. Loan amounts from ₹1 lakh, interest rates from 6.5% p.a. Discreet, relationship-led support."
        canonicalUrl="https://www.ojovest.com/apply-loan"
        image="/og-image.png"
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-[#354F37]">
            OJOVEST
          </Link>

          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#20BA5A] transition"
          >
            <Phone size={18} />
            WhatsApp
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm text-[#444444]">
            <ShieldCheck size={16} className="text-[#354F37]" />
            Confidential • Fast • Best-fit Lender
          </div>

          <h1 className="mt-6 text-4xl md:text-6xl font-bold text-[#222222] leading-tight">
            Apply for a Loan
            <br />
            <span className="text-[#354F37]">from ₹1 Lakh</span>
          </h1>

          <p className="mt-5 text-lg md:text-xl text-[#444444] max-w-3xl mx-auto">
            Premium loan assistance with relationship-led support. Rates start
            from <b>6.5% p.a.*</b> (profile-based). We guide you with documents,
            structuring and lender selection.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#apply"
              className="bg-[#354F37] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#426045] transition inline-flex items-center justify-center gap-2"
            >
              Start Application <BadgeCheck size={18} />
            </a>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-[#354F37] text-[#354F37] px-8 py-4 rounded-lg font-semibold hover:bg-[#F8F4EF] transition inline-flex items-center justify-center gap-2"
            >
              Talk to Advisor <Phone size={18} />
            </a>
          </div>

          <p className="mt-4 text-xs text-[#666]">
            *Final rates & approvals depend on lender policies and your profile.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <h2 className="text-3xl font-bold text-[#222222] text-center mb-8">
          Simple 3-Step Process
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <ClipboardList className="text-[#354F37] mb-4" size={40} />
            <h3 className="text-xl font-bold text-[#222222] mb-2">
              1) Share Details
            </h3>
            <p className="text-[#444444]">
              Amount, loan type, city and basic profile. We keep it confidential.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <FileText className="text-[#354F37] mb-4" size={40} />
            <h3 className="text-xl font-bold text-[#222222] mb-2">
              2) Document Checklist
            </h3>
            <p className="text-[#444444]">
              We tell exactly what to share and help you arrange it correctly.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <Timer className="text-[#354F37] mb-4" size={40} />
            <h3 className="text-xl font-bold text-[#222222] mb-2">
              3) Faster Processing
            </h3>
            <p className="text-[#444444]">
              Best-fit lender mapping and priority handling for quick decisioning.
            </p>
          </div>
        </div>
      </section>

      {/* Document Checklist */}
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#222222] text-center mb-10">
            Documents Checklist (Typical)
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-gray-200 rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-[#222222] mb-4">
                Salaried
              </h3>
              <ul className="space-y-3 text-[#444444]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  PAN & Aadhaar
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Salary slips (latest)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Bank statements (6 months)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Address proof
                </li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-[#222222] mb-4">
                Self-employed / Business
              </h3>
              <ul className="space-y-3 text-[#444444]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  PAN, Aadhaar, Business proof
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  ITR (2–3 years)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Bank statements (6–12 months)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Financials (if applicable)
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 bg-[#F8F4EF] border border-gray-200 rounded-xl p-6 text-[#444444]">
            <p className="font-semibold text-[#222222] mb-2">Safety Note</p>
            <p className="text-sm leading-relaxed">
              OJOVEST never asks for OTP, PIN, CVV or passwords. Please share
              documents only through verified channels.
            </p>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-16 bg-[#F8F4EF]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#222222] text-center mb-4">
            Start Your Application
          </h2>
          <p className="text-[#444444] text-center mb-10">
            Fill the form and our team will connect within 24 hours.
          </p>

          <form
            onSubmit={handleSubmit}
            className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[#222222] mb-2 font-semibold">
                  Full Name *
                </label>
                <input
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-[#222222] mb-2 font-semibold">
                  Phone Number *
                </label>
                <input
                  name="phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="10 digits or +91XXXXXXXXXX"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#222222] mb-2 font-semibold">
                Email (optional)
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                placeholder="you@email.com"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[#222222] mb-2 font-semibold">
                  Loan Type *
                </label>
                <select
                  name="loanType"
                  value={form.loanType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                >
                  <option value="personal_loan">Personal Loan</option>
                  <option value="home_loan">Home Loan</option>
                  <option value="lap">Loan Against Property (LAP)</option>
                  <option value="business_loan">Business Loan</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-[#222222] mb-2 font-semibold">
                  City
                </label>
                <input
                  name="city"
                  type="text"
                  value={form.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="Mumbai, Delhi, Bengaluru..."
                />
              </div>
            </div>

            <div>
              <label className="block text-[#222222] mb-2 font-semibold">
                Loan Amount (min ₹1,00,000) *
              </label>
              <input
                name="loanAmount"
                type="number"
                min="100000"
                step="1000"
                required
                value={form.loanAmount}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                placeholder="100000"
              />
              <p className="text-xs text-[#666] mt-2">
                Rates start from 6.5% p.a.* (profile-based).
              </p>
            </div>

            <div>
              <label className="block text-[#222222] mb-2 font-semibold">
                Notes / Requirement
              </label>
              <textarea
                name="message"
                rows="4"
                value={form.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                placeholder="Tell us salary/business turnover, existing loan, purpose, timeline..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full px-6 py-4 rounded-lg font-semibold transition ${
                loading
                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                  : "bg-[#354F37] text-white hover:bg-[#426045]"
              }`}
            >
              {loading ? "Submitting..." : "Submit Loan Request"}
            </button>

            <div className="text-xs text-[#666] flex items-start gap-2">
              <CheckCircle2 className="mt-[2px]" size={14} />
              <span>
                By submitting, you agree to be contacted by OJOVEST via call,
                WhatsApp or email. We never ask for OTP/PIN/CVV.
              </span>
            </div>
          </form>

          <div className="mt-8 text-center">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#354F37] font-semibold hover:underline"
            >
              <Phone size={18} /> Prefer WhatsApp? Click here
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#354F37] py-8 text-center text-gray-200">
        <p className="font-semibold text-white">OJOVEST</p>
        <p className="text-sm mt-2">
          Premium Loans • Confidential Processing • Fast Turnaround
        </p>
        <p className="text-xs mt-4">
          © {new Date().getFullYear()} OJOVEST. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default LoanApplication;
